import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Copy, KeyRound, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NO_TEMPLATE = "__none__"; // Select can't hold an empty-string item value

export default function Settings() {
  const { user } = useAuth();
  const [wa, setWa] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    jalpi_api_key: "", // blank = keep the stored key
    jalpi_instance_number: "",
    mock_mode: true,
    general_reminder_template_id: NO_TEMPLATE,
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  async function load() {
    const [{ data }, { data: tpls }] = await Promise.all([
      api.get("/settings/whatsapp"),
      api.get("/templates"),
    ]);
    setWa(data);
    setTemplates(tpls);
    setForm({
      jalpi_api_key: "",
      jalpi_instance_number: data.jalpi_instance_number || "",
      mock_mode: !!data.mock_mode,
      general_reminder_template_id: data.general_reminder_template_id || NO_TEMPLATE,
    });
  }
  useEffect(() => {
    load();
  }, []);

  const approvedTemplates = templates.filter((t) => t.approval_status === "approved");

  async function saveWa(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const changes = {
        jalpi_instance_number: form.jalpi_instance_number,
        mock_mode: !!form.mock_mode,
        general_reminder_template_id:
          form.general_reminder_template_id === NO_TEMPLATE
            ? ""
            : form.general_reminder_template_id,
      };
      if (form.jalpi_api_key) changes.jalpi_api_key = form.jalpi_api_key;
      const { data } = await api.post("/settings/whatsapp", changes);
      if (data.webhook_error) {
        toast.warning(`Saved, but webhook registration failed: ${data.webhook_error}`);
      } else if (data.webhook_registered) {
        toast.success("Settings saved and webhook registered with Jalpi");
      } else {
        toast.success("WhatsApp settings saved");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function testKey() {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/whatsapp/test", {
        jalpi_api_key: form.jalpi_api_key || null,
      });
      toast.success(
        `Key is valid${data.templates_count != null ? ` — ${data.templates_count} templates found` : ""}`,
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  }

  async function changePw(e) {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const webhookUrl = wa?.webhook_url || "";

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-10" data-testid="settings-page">
      <PageHeader
        overline="Configuration"
        title="Settings"
        subtitle="Connect your Jalpi WhatsApp instance and manage your account."
      />

      <Card className="mt-8 border-border p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Integration
            </div>
            <h2 className="mt-1 font-heading text-xl font-bold">WhatsApp via Jalpi</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Connect your Jalpi instance to send and receive WhatsApp messages. Leave the API
              key empty to run in mock mode.
            </p>
          </div>
          <Badge
            className={`rounded-full ${wa?.mock_mode ? "bg-fun-yellow-tint text-fun-yellow" : "bg-fun-green-tint text-fun-green"}`}
          >
            {wa?.mock_mode ? "Mock mode" : "Live"}
          </Badge>
        </div>

        <form onSubmit={saveWa} className="mt-6 space-y-5" data-testid="wa-settings-form">
          <div className="space-y-2">
            <Label>
              Jalpi API key{" "}
              {wa?.jalpi_api_key_masked && (
                <span className="ml-2 text-xs text-muted-foreground">
                  current: <span className="font-mono">{wa.jalpi_api_key_masked}</span>
                </span>
              )}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="password"
                value={form.jalpi_api_key}
                onChange={(e) => setForm({ ...form, jalpi_api_key: e.target.value })}
                placeholder={
                  wa?.jalpi_api_key_set
                    ? "Enter to update; leave blank to keep current"
                    : "Your Jalpi instance API key"
                }
                data-testid="jalpi-api-key"
              />
              <Button
                type="button"
                variant="outline"
                onClick={testKey}
                disabled={testing}
                data-testid="jalpi-test-btn"
              >
                {testing ? "Testing…" : "Test key"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Find this in your Jalpi dashboard under your WhatsApp instance.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Instance number</Label>
            <Input
              value={form.jalpi_instance_number}
              onChange={(e) => setForm({ ...form, jalpi_instance_number: e.target.value })}
              placeholder="Your WhatsApp number with country code, e.g. 919876543210"
              data-testid="jalpi-instance-number"
            />
            <p className="text-xs text-muted-foreground">
              Optional — used to filter out echo notifications of your own sends.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 p-3">
            <div>
              <div className="text-sm font-semibold">Mock mode</div>
              <div className="text-xs text-muted-foreground">
                Simulate sending — messages are logged but never leave the server.
              </div>
            </div>
            <Switch
              checked={form.mock_mode}
              onCheckedChange={(v) => setForm({ ...form, mock_mode: v })}
              data-testid="wa-mock-switch"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5" /> General reminder template
            </Label>
            <Select
              value={form.general_reminder_template_id}
              onValueChange={(v) => setForm({ ...form, general_reminder_template_id: v })}
            >
              <SelectTrigger data-testid="general-reminder-template-select">
                <SelectValue placeholder="No general reminder template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TEMPLATE}>None</SelectItem>
                {approvedTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Used by "Send reminders" for any programme that hasn't set its own reminder
              template. Only approved templates can be selected.
            </p>
          </div>

          {webhookUrl && (
            <div className="rounded-lg border border-dashed border-border bg-accent/40 p-4">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                Jalpi webhook URL
              </div>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 truncate rounded-md bg-white/60 px-2 py-1 font-mono text-xs">
                  {webhookUrl}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(webhookUrl);
                    toast.success("Copied");
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Registered with Jalpi automatically when you save. It must be publicly reachable
                over HTTPS — incoming messages arrive here.
              </p>
            </div>
          )}

          <Button type="submit" disabled={saving} data-testid="wa-save-btn">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </form>
      </Card>

      <Card className="mt-6 border-border p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-fun-red" />
          <h2 className="font-heading text-lg font-bold">Change password</h2>
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          Signed in as <span className="font-mono">{user?.email}</span>
        </div>
        <form onSubmit={changePw} className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="pw-form">
          <div className="space-y-2">
            <Label>Current password</Label>
            <Input
              type="password"
              required
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              data-testid="pw-current"
            />
          </div>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              data-testid="pw-new"
            />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" data-testid="pw-submit">
              Update password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
